import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useProfileState } from '@/contexts/ProfileContext';

/* ============================================================
   Full Profile Report — printable / PDF export
   Renders EVERYTHING from the analysis (Overview + Skills + Roles
   + Blind spots + Hidden strengths + Translate + Milestones +
   Network + Events) in a single long document that is optimised
   for printing to PDF.

   Opened in a new tab via `/dashboard/report?print=1`. When the
   `print=1` query param is present, the browser print dialog is
   triggered automatically once the analysis has loaded.
   ============================================================ */

export default function PrintReport() {
  const { analysis, savedProfile, loading } = useProfileState();
  const location = useLocation();

  const c1 = analysis?.call1 || {};
  const c2 = analysis?.call2 || {};
  const shouldAutoPrint = new URLSearchParams(location.search).get('print') === '1';

  useEffect(() => {
    if (loading || !analysis || !shouldAutoPrint) return;
    // Wait a tick for images / fonts / layout to settle, then trigger print.
    const t = setTimeout(() => {
      try { window.print(); } catch (e) { /* no-op */ }
    }, 700);
    return () => clearTimeout(t);
  }, [loading, analysis, shouldAutoPrint]);

  if (loading) {
    return (
      <div className="sd-print-loading">
        <p>Loading your profile...</p>
      </div>
    );
  }
  if (!analysis) {
    return (
      <div className="sd-print-loading">
        <p>No analysis found. Run an analysis first, then try Export again.</p>
      </div>
    );
  }

  const name = savedProfile?.name || c1.name_first || 'Your profile';
  const generatedAt = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <article className="sd-print-report" data-testid="print-report">
      {/* Cover */}
      <header className="sd-print-cover">
        <div className="sd-print-brand">SD·Pivot</div>
        <div className="sd-print-cover-eyebrow">Career intelligence report</div>
        <h1 className="sd-print-cover-title">{name}</h1>
        {c1.tagline && <p className="sd-print-cover-tagline">{c1.tagline}</p>}
        {c1.headline && <p className="sd-print-cover-headline">{c1.headline}</p>}
        {(c1.hero_stats || []).length > 0 && (
          <div className="sd-print-cover-stats">
            {c1.hero_stats.map((s, i) => (
              <div key={i} className="sd-print-cover-stat">
                <div className="lbl">{s.label}</div>
                <div className="val">{s.value}</div>
              </div>
            ))}
          </div>
        )}
        <div className="sd-print-cover-meta">
          <span>{savedProfile?.location || ''}</span>
          <span>Generated {generatedAt}</span>
        </div>
      </header>

      {/* Skills Profile */}
      {(c1.skill_web || []).length > 0 && (
        <Section title="Skills profile" subtitle="How you score against the sustainability market on 6 core axes.">
          <div className="sd-print-skills">
            {c1.skill_web.map((s, i) => (
              <div key={i} className="sd-print-skill-row">
                <div className="sd-print-skill-head">
                  <span className="axis">{s.axis}</span>
                  <span className="score">{s.score}/10</span>
                </div>
                <div className="sd-print-skill-bar">
                  <div className="fill" style={{ width: `${Math.max(0, Math.min(10, Number(s.score) || 0)) * 10}%` }} />
                </div>
                {s.detail && <p className="sd-print-skill-detail">{s.detail}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Role paths */}
      {(c1.roles || []).length > 0 && (
        <Section title="Role paths" subtitle="Five roles that match your background, scored honestly on evidence.">
          <div className="sd-print-roles">
            {c1.roles.map((r, i) => (
              <div key={i} className="sd-print-role">
                <div className="sd-print-role-head">
                  <div className="sd-print-role-title">
                    <h3>{r.title}</h3>
                    <div className="sd-print-role-meta">
                      <span className={`type type-${(r.type || '').toLowerCase()}`}>{r.type}</span>
                      {r.sector && <span className="sector">{r.sector}</span>}
                    </div>
                  </div>
                  <div className="sd-print-role-match">
                    <div className="pct">{r.match_pct}%</div>
                    <div className="lbl">match</div>
                  </div>
                </div>
                {r.why && <p className="sd-print-role-why">{r.why}</p>}
                {(r.salary_ranges || []).length > 0 && (
                  <div className="sd-print-role-salary">
                    {r.salary_ranges.slice(0, 3).map((s, j) => (
                      <div key={j} className="row">
                        <span className="range">{s.range}</span>
                        <span className="geo">{s.geo}</span>
                        {s.source && <span className="src">· {s.source}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {(r.skills_present || r.skills_gap) && (
                  <div className="sd-print-role-skills">
                    {r.skills_present?.length > 0 && (
                      <div className="col">
                        <div className="col-lbl">Skills you have</div>
                        <ul>{r.skills_present.map((s, j) => <li key={j}>{s}</li>)}</ul>
                      </div>
                    )}
                    {r.skills_gap?.length > 0 && (
                      <div className="col">
                        <div className="col-lbl">Skills to build</div>
                        <ul>{r.skills_gap.map((s, j) => <li key={j}>{s}</li>)}</ul>
                      </div>
                    )}
                  </div>
                )}
                {r.key_gap && (
                  <div className="sd-print-role-gap">
                    <span className="lbl">Key gap · </span>{r.key_gap}
                  </div>
                )}
                {(r.companies_hiring || []).length > 0 && (
                  <div className="sd-print-role-hiring">
                    <div className="lbl">Hiring signals</div>
                    <ul>
                      {r.companies_hiring.map((h, j) => (
                        <li key={j}><strong>{h.name}</strong>{h.signal ? ` — ${h.signal}` : ''}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Salary landscape */}
      {c1.salary_landscape && Object.keys(c1.salary_landscape).length > 0 && (
        <Section title="Salary landscape" subtitle="Your current market range, the next move, and the stretch role.">
          <div className="sd-print-salary">
            <div className="sd-print-salary-row"><span className="lbl">Current range</span><span className="val">{c1.salary_landscape.current_range || '—'}</span></div>
            <div className="sd-print-salary-row"><span className="lbl">Next move range</span><span className="val">{c1.salary_landscape.next_move_range || '—'}</span></div>
            <div className="sd-print-salary-row"><span className="lbl">Stretch range</span><span className="val">{c1.salary_landscape.stretch_range || '—'}</span></div>
            {c1.salary_landscape.time_to_competitive_months && (
              <div className="sd-print-salary-row"><span className="lbl">Time to competitive</span><span className="val">{c1.salary_landscape.time_to_competitive_months}</span></div>
            )}
            {c1.salary_landscape.source && (
              <div className="sd-print-salary-src">Source: {c1.salary_landscape.source}</div>
            )}
          </div>
          {c1.salary_alert && <p className="sd-print-salary-alert">{c1.salary_alert}</p>}
        </Section>
      )}

      {/* Gaps to close */}
      {((c1.gaps_critical || []).length > 0 || (c1.gaps_important || []).length > 0) && (
        <Section title="Gaps to close" subtitle="Named resources for the specific credentials and skills you need.">
          {(c1.gaps_critical || []).length > 0 && (
            <div className="sd-print-gaps-group">
              <h3 className="sd-print-gaps-head sd-print-gaps-head--critical">Critical gaps</h3>
              <GapList items={c1.gaps_critical} />
            </div>
          )}
          {(c1.gaps_important || []).length > 0 && (
            <div className="sd-print-gaps-group">
              <h3 className="sd-print-gaps-head sd-print-gaps-head--important">Important gaps</h3>
              <GapList items={c1.gaps_important} />
            </div>
          )}
        </Section>
      )}

      {/* Hidden strengths */}
      {(c2.hidden_strengths || []).length > 0 && (
        <Section title="Hidden strengths" subtitle="Under-recognised assets you already carry into every conversation.">
          <div className="sd-print-strengths">
            {c2.hidden_strengths.slice(0, 3).map((h, i) => (
              <div key={i} className="sd-print-strength">
                <h4>{h.headline}</h4>
                {h.reasoning && <p className="reasoning">{h.reasoning}</p>}
                {h.how_to_use && (
                  <div className="how-to">
                    <span className="lbl">How to use it</span>
                    <p>{h.how_to_use}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Blind spots */}
      {(c1.blind_spots || []).length > 0 && (
        <Section title="Blind spots worth checking" subtitle="Where the honest market data pushes back on common assumptions.">
          <div className="sd-print-blindspots">
            {c1.blind_spots.slice(0, 3).map((b, i) => (
              <div key={i} className="sd-print-blindspot">
                <h4>{b.title}</h4>
                {b.explanation && <p>{b.explanation}</p>}
                {b.action && (
                  <div className="the-move">
                    <span className="lbl">The move</span>
                    <p>{b.action}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Translate for the room */}
      {(c1.cross_sector_translation || []).length > 0 && (
        <Section title="Translate for the room" subtitle="The same experience, told three ways: as you know it, for the sustainability room, for the business room.">
          <div className="sd-print-translate">
            {c1.cross_sector_translation.map((x, i) => (
              <div key={i} className="sd-print-translate-card">
                <div className="face face--exp">
                  <div className="lbl">Your experience</div>
                  <p>{x.user_experience || x.background || '—'}</p>
                </div>
                <div className="face face--sust">
                  <div className="lbl">Speak sustainability</div>
                  <p>{x.speak_sustainability || x.sustainability_equivalent || x.translation || '—'}</p>
                </div>
                <div className="face face--biz">
                  <div className="lbl">Speak business</div>
                  <p>{x.speak_business || '—'}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Milestones (My Progress) */}
      {((c2.milestones_critical || []).length > 0 || (c2.milestones_quick || []).length > 0 || (c2.milestones_build || []).length > 0) && (
        <Section title="Your 90-day plan" subtitle="The specific milestones that will move your pivot forward.">
          {(c2.milestones_critical || []).length > 0 && (
            <MilestonesGroup title="Critical milestones" items={c2.milestones_critical} />
          )}
          {(c2.milestones_quick || []).length > 0 && (
            <MilestonesGroup title="Quick wins" items={c2.milestones_quick} />
          )}
          {(c2.milestones_build || []).length > 0 && (
            <MilestonesGroup title="Skill building" items={c2.milestones_build} />
          )}
        </Section>
      )}

      {/* People to meet */}
      {(c2.network || []).length > 0 && (
        <Section title="People to meet" subtitle="Specific outreach targets with why-valuable and message drafts.">
          <div className="sd-print-network">
            {c2.network.map((p, i) => (
              <div key={i} className="sd-print-person">
                <div className="head">
                  <h4>{p.name}</h4>
                  <span className="role">{p.role}</span>
                </div>
                {p.connection_path && <div className="path">{p.platform} · {p.connection_path}</div>}
                {p.why_valuable && (
                  <div className="why">
                    <span className="lbl">Why valuable</span>
                    <p>{p.why_valuable}</p>
                  </div>
                )}
                {(p.variants || []).length > 0 && (
                  <div className="messages">
                    <span className="lbl">Message drafts</span>
                    {p.variants.map((v, j) => (
                      <div key={j} className="msg">
                        <div className="angle">{v.angle}</div>
                        <p>{v.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Events & Resources */}
      {(c2.events_resources || []).length > 0 && (
        <Section title="Events and resources" subtitle="Where the field gathers and where to keep learning.">
          <div className="sd-print-events">
            {c2.events_resources.map((e, i) => (
              <div key={i} className="sd-print-event">
                <div className="head">
                  <h4>{e.name}</h4>
                  <span className="type">{e.type}</span>
                </div>
                <div className="meta">
                  {e.when && <span>{e.when}</span>}
                  {e.location && <span>{e.location}</span>}
                  {e.cost && <span>{e.cost}</span>}
                </div>
                {e.relevance && <p>{e.relevance}</p>}
                {e.url && <div className="url">{e.url}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Footer */}
      <footer className="sd-print-footer">
        <div className="brand">SD·Pivot · Career intelligence for sustainability professionals</div>
        <div className="date">Generated {generatedAt}</div>
      </footer>
    </article>
  );
}

/* ---------- helpers ---------- */

function Section({ title, subtitle, children }) {
  return (
    <section className="sd-print-section">
      <div className="sd-print-section-head">
        <h2 className="sd-print-section-title">{title}</h2>
        {subtitle && <p className="sd-print-section-sub">{subtitle}</p>}
      </div>
      <div className="sd-print-section-body">{children}</div>
    </section>
  );
}

function GapList({ items }) {
  return (
    <div className="sd-print-gaps-list">
      {items.map((g, i) => (
        <div key={i} className="sd-print-gap">
          <div className="head">
            <h4>{g.gap}</h4>
            {g.duration && <span className="dur">{g.duration}</span>}
          </div>
          {g.resource && (
            <div className="resource">
              <strong>{g.resource}</strong>
              {g.provider && <span> · {g.provider}</span>}
              {g.cost && <span> · {g.cost}</span>}
            </div>
          )}
          {g.why && <p className="why">{g.why}</p>}
          {g.url && <div className="url">{g.url}</div>}
        </div>
      ))}
    </div>
  );
}

function MilestonesGroup({ title, items }) {
  return (
    <div className="sd-print-milestones-group">
      <h3 className="sd-print-milestones-head">{title}</h3>
      <ol className="sd-print-milestones-list">
        {items.map((m, i) => (
          <li key={i} className="sd-print-milestone">
            <div className="mstone-head">
              <h4>{m.title}</h4>
              {m.time_estimate && <span className="time">{m.time_estimate}</span>}
            </div>
            {m.brief && <p>{m.brief}</p>}
            {m.resource_url && <div className="url">{m.resource_url}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
}
